import PayLink from '../../assets/docs/one-time-link.png';
import InvoiceImage from '../../assets/invoice-image.png';
import CheckoutImage from '../../assets/checkout-image.png';
import SubscriptionImage from '../../assets/subscription-image.png';
import PayoutImage from '../../assets/payout-image.png';
import SettlementImage from '../../assets/settlement-image.png';
import TransactionImage from '../../assets/transaction-image.png';
import CustomerImage from '../../assets/customer-image.png';
import ActivityImage from '../../assets/activity-log-image.png';
import TestAndLive from '../../assets/test_and_live_mode.svg';
import HeroImage from '../../assets/Hero-image.png';


export const HERO_IMAGE = HeroImage; // Dashboard overview screenshot

export const PAYMENT_METHODS = [
    ['Dynamic virtual account', 'A one-time account number for each payment.'],
    ['Bank transfer', 'From any mobile or internet banking app.'],
    ['Card', 'Visa, Mastercard and Verve.'],
    ['USSD', 'No internet or smartphone needed.'],
    ['PayAttitude', 'Pay with the phone number linked to a bank account.'],
];

export const SECTIONS = [
    {
        id: 'get-paid',
        nav: 'Get paid',
        navHint: 'Links, invoices, subscriptions',
        title: 'Get paid without building anything',
        intro: 'You don’t need a website or a developer to start collecting money. Create a payment from your dashboard and send it to your customer.',
        spotlights: [
            {
                title: 'Payment links',
                image: PayLink,
                imageLabel: 'Payment links',
                body: 'Create a link, set the amount and share it on WhatsApp, SMS, email or social media. The amount is fixed, so your customer pays exactly what you asked for.',
                points: [
                    'One-time links for a single payment',
                    'Subscription links that charge weekly, monthly or on the schedule you set',
                    'Every payment shows up in your dashboard as it happens',
                ],
                link: { to: '/payment-link', label: 'How payment links work' },
            },
            {
                title: 'Invoices',
                image: InvoiceImage,
                imageLabel: 'Invoices',
                body: 'Send an itemised invoice your customer can pay online. Pick a saved customer or add their details as you go, and download any invoice as a PDF.',
                points: ['Itemised bills with a pay-online link', 'Saved customers fill in automatically', 'PDF copy for your records'],
            },
            {
                title: 'Subscriptions and plans',
                image: SubscriptionImage,
                imageLabel: 'Subscriptions and plans',
                body: 'Set up plans for anything you bill on repeat — memberships, school fees, rent or instalments — and WayaQuick charges your customers on schedule.',
                points: [
                    'Create plans with their own amount and billing interval',
                    'Choose whether customers can cancel their own subscriptions',
                    'Customers are charged automatically on schedule',
                ],
            },
        ],
    },
    {
        id: 'checkout',
        nav: 'Checkout',
        navHint: 'Five ways to pay',
        title: 'A checkout your customers can trust',
        intro: 'Whether they pay from a link, an invoice, your website or your app, customers land on the same secure WayaQuick checkout.',
        spotlights: [
            {
                title: 'Hosted checkout',
                image: CheckoutImage,
                imageLabel: 'Checkout page',
                body: 'Customers enter their payment details on WayaQuick, not on your website, so you never handle card details yourself. They choose the method that suits them and see the result straight away.',
                points: [
                    'Turn payment methods on or off in Payment Preferences',
                    'Customers can get an email receipt for every payment',
                    'The same checkout for links, invoices, websites and apps',
                ],
            },
        ],
        methods: true,
        items: [
            {
                title: 'Exact amounts only',
                body: 'A payment for more or less than the amount requested is rejected automatically, and any money that came through is reversed to the customer.',
            },
            {
                title: 'Fewer abandoned payments',
                body: 'If a card is declined or a bank app is down, the customer can switch to another method without leaving the checkout.',
            },
        ],
    },
    {
        id: 'money-out',
        nav: 'Money out',
        navHint: 'Settlement, payouts, refunds',
        title: 'Your money, when you need it',
        intro: 'Choose how fast you get settled, pay suppliers and staff, and handle refunds and disputes from the same dashboard.',
        spotlights: [
            {
                title: 'Settlement',
                image: SettlementImage,
                imageLabel: 'Settlement settings',
                body: 'Get settled instantly into your WayaBank account, or daily (T+1) or weekly into any other bank account. Add more than one settlement account and choose your default.',
                points: [
                    'Instant settlement into a WayaBank account',
                    'Daily (T+1) or weekly settlement into other banks',
                    'Switch your default account at any time',
                ],
            },
            {
                title: 'Payouts',
                image: PayoutImage,
                imageLabel: 'Payouts',
                body: 'Send money to any Nigerian bank account from your dashboard. You see the account name before you send, so money goes to the right person.',
                points: [
                    'Account name confirmed before every payout',
                    'Optional OTP confirmation on every payout',
                    'Payout receipts for you and your beneficiaries',
                ],
            },
        ],
        items: [
            {
                title: 'Refunds in two clicks',
                body: 'Open a successful transaction and click Refund Customer. The payout opens with the customer and amount already filled in.',
            },
            {
                title: 'Disputes',
                body: 'When a customer disputes a payment, review the details in your dashboard and accept or reject it.',
            },
            {
                title: 'Automatic reversals',
                body: 'Wrong-amount payments are returned to the customer for you. There’s nothing to refund or reconcile.',
            },
        ],
    },
    {
        id: 'manage',
        nav: 'Run your business',
        navHint: 'Dashboard, customers, team',
        title: 'Everything about your business in one dashboard',
        intro: 'See what’s happening as it happens, keep your customer records tidy and give your team the access they need — no more.',
        spotlights: [
            {
                title: 'Transactions and reporting',
                image: TransactionImage,
                imageLabel: 'Dashboard and transactions',
                body: 'Follow every payment in real time, with a clear status for each one. Open any transaction to see its full details and act on it from there.',
                points: [
                    'Live transaction list with clear statuses',
                    'Full details for every transaction',
                    'Refund or follow up without leaving the page',
                ],
            },
            {
                title: 'Customers',
                image: CustomerImage,
                imageLabel: 'Customers',
                body: 'Keep all your customers in one searchable list. Use them again when you create invoices, so you don’t have to type their details twice.',
                points: ['Search your full customer list', 'Reuse customers on invoices'],
            },
            {
                title: 'Activity log',
                image: ActivityImage,
                imageLabel: 'Activity log',
                body: 'A plain-language record of what happened on your account and who did it. Open any entry for the full details.',
                points: ['Readable descriptions, not system codes', 'Details for every activity', 'Visible only to people you allow'],
            },
        ],
        items: [
            {
                title: 'Team members and roles',
                body: 'Invite your team and control what each person can do, from managing payouts to changing settlement accounts.',
            },
            {
                title: 'Branches',
                body: 'Add branches to your account if your business operates from more than one location.',
            },
            {
                title: 'Notifications',
                body: 'Get an email or sms for every transaction, and choose whether alerts go to your business email, your whole team or specific people.',
            },
            
        ],
    },
    {
        id: 'developers',
        nav: 'Developers',
        navHint: 'API, SDKs, plugins',
        title: 'Built in, wherever you sell online',
        intro: 'Add WayaQuick to your website, app or backend with a plugin, an SDK or the API — and test everything before a single naira moves.',
        dark: true,
        spotlights: [
            {
                title: 'Test and Live mode',
                image: TestAndLive,
                imageLabel: 'Test and Live mode',
                body: 'Every account starts in Test mode, with its own API keys, so you can build and try every flow with test money. Live mode unlocks when your KYC is approved.',
                points: ['Separate test and live secret keys', 'Test card details to try payments end to end', 'Switch modes from your dashboard once approved'],
                link: { to: '/test-environment', label: 'About the test environment' },
            },
        ],
        integrations: [
            { title: 'WooCommerce plugin', body: 'Install from the WordPress plugin directory, add your keys and take payments at checkout.', to: '/sdks/woocommerce' },
            { title: 'Server SDKs', body: 'PHP, .NET, Java, Node.js and Go.', to: '/sdks?type=server' },
            { title: 'Mobile SDKs', body: 'iOS, Android, Flutter and React Native.', to: '/sdks?type=mobile' },
            { title: 'Standard API', body: 'Collections, payouts, account name lookup, BVN verification and webhooks from any language.', to: '/standard-api-integration' },
        ],
    },
    {
        id: 'security',
        nav: 'Security',
        navHint: 'KYC, 2FA, PIN',
        title: 'Security at every step',
        intro: 'Your account, your money and your customers’ details are protected from sign-up to settlement.',
        items: [
            { title: 'Verified businesses', body: 'Every merchant completes KYC, including BVN verification, before taking live payments.' },
            { title: 'Login ID', body: 'You sign in with a private Login ID sent to your email, not just your email address.' },
            { title: 'Two-factor authentication', body: 'Turn on 2FA to confirm each sign-in with a code sent to your email.' },
            { title: 'Transaction PIN', body: 'A PIN only you know adds a second check on sensitive actions.' },
            { title: 'Payout OTP', body: 'Require a one-time code before any money leaves your account.' },
            { title: 'Role-based access', body: 'Team members only see and do what their role allows.' },
        ],
        link: { to: '/compliance', label: 'Read about our compliance' },
    },
];

